import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextItem, ContextItemKind } from '../../types';
import { getGraphQLClient, SAVE_CONTEXT_ITEM, DELETE_CONTEXT_ITEM } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { Button, Input, AutoResizeTextarea } from '../common';

const CONTEXT_KINDS: ContextItemKind[] = ['fact', 'quote', 'interview_excerpt', 'research'];

export interface ContextItemsPanelProps {
  contextableType: 'Scene';
  contextableId: string;
  items: ContextItem[];
  onChanged: () => Promise<void> | void;
  onError: (msg: string) => void;
  title?: string;
  emptyHint?: string;
}

export function ContextItemsPanel({
  contextableType,
  contextableId,
  items,
  onChanged,
  onError,
  title,
  emptyHint,
}: ContextItemsPanelProps) {
  const { t } = useTranslation();
  const [draftContent, setDraftContent] = useState('');
  const [draftKind, setDraftKind] = useState<ContextItemKind>('fact');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const addItem = async () => {
    if (!draftContent.trim()) return;
    setSubmitting(true);
    try {
      const client = getGraphQLClient();
      await client.request(SAVE_CONTEXT_ITEM, {
        contextableType,
        contextableId,
        kind: draftKind,
        content: draftContent.trim(),
      });
      setDraftContent('');
      await onChanged();
    } catch (err) {
      captureException(err);
      onError(t('common.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const removeItem = async (item: ContextItem) => {
    try {
      const client = getGraphQLClient();
      await client.request(DELETE_CONTEXT_ITEM, { contextItemId: item.id });
      await onChanged();
    } catch (err) {
      captureException(err);
      onError(t('common.error'));
    }
  };

  const saveEdit = async (item: ContextItem) => {
    if (!editContent.trim()) return;
    try {
      const client = getGraphQLClient();
      await client.request(SAVE_CONTEXT_ITEM, {
        contextItemId: item.id,
        content: editContent.trim(),
      });
      setEditingId(null);
      setEditContent('');
      await onChanged();
    } catch (err) {
      captureException(err);
      onError(t('common.error'));
    }
  };

  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
        {title ?? t('chaptersFirst.contextItems', 'Supporting material')}
      </h3>

      <ul className="space-y-2 mb-4">
        {items.length === 0 && (
          <li className="text-sm text-gray-500">
            {emptyHint ??
              t(
                'chaptersFirst.noContextItems',
                'Add notes, facts, or stories to guide regeneration.'
              )}
          </li>
        )}
        {items.map((item) => {
          const isEditing = editingId === item.id;
          return (
            <li
              key={item.id}
              className="border border-gray-200 dark:border-gray-700 rounded-md p-3 bg-white dark:bg-gray-800"
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="text-xs uppercase tracking-wide text-indigo-700 dark:text-indigo-300">
                  {t(`chaptersFirst.kind.${item.kind}`, item.kind)}
                </span>
                <div className="flex gap-1">
                  {!isEditing && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingId(item.id);
                        setEditContent(item.content);
                      }}
                    >
                      {t('common.edit', 'Edit')}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => removeItem(item)}>
                    {t('common.delete', 'Delete')}
                  </Button>
                </div>
              </div>
              {isEditing ? (
                <div className="space-y-2">
                  <AutoResizeTextarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    minRows={2}
                  />
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditingId(null);
                        setEditContent('');
                      }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button size="sm" variant="primary" onClick={() => saveEdit(item)}>
                      {t('common.save', 'Save')}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                  {item.content}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
        <div className="flex flex-col gap-2">
          <select
            value={draftKind}
            onChange={(e) => setDraftKind(e.target.value as ContextItemKind)}
            className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
          >
            {CONTEXT_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`chaptersFirst.kind.${k}`, k)}
              </option>
            ))}
          </select>
          <Input
            value={draftContent}
            onChange={(e) => setDraftContent(e.target.value)}
            placeholder={t(
              'chaptersFirst.addContextPlaceholder',
              'Add a note, fact, quote, or memory...'
            )}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                addItem();
              }
            }}
          />
          <Button
            variant="primary"
            onClick={addItem}
            disabled={submitting || !draftContent.trim()}
            isLoading={submitting}
          >
            {t('common.add', 'Add')}
          </Button>
        </div>
      </div>
    </div>
  );
}
